import { useContext, useMemo, useState } from 'react'

import { Collapse, List, Modal, Switch, Tooltip } from 'antd'
import { useSelector } from 'react-redux'
import { faEdit, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { selectUser } from '../../reducers/UserReducer'
import { InstitutionOptions, OPTIONS_WITH_DAY_VALUES } from '../../utils/constants'
import OptionValueModal from './OptionValueModal'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import { updateInstitutionOption } from '../../utils/api/institution'
import { onError, onSuccess } from '../../utils/apiHelper'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import PremiumIcon from '../shared/Icons/PremiumIcon'
import IconButton from '../antd/Buttons/IconButton'

const OPTIONS_WITH_CUSTOM_VALUES = [
  ...OPTIONS_WITH_DAY_VALUES,
  InstitutionOptions.SEND_MATCHING_HOSPITAL_EMAIL,
  InstitutionOptions.STUDENT_INTERNSHIP_REQUEST_LIMIT
]

/** Displays the current value of an option, suffixed by its unit when it has one */
const displayOptionValue = (option, t) => {
  if (!option.optionValue) {
    return t('common.not_set')
  }

  return OPTIONS_WITH_DAY_VALUES.includes(option.optionType.type)
    ? `${option.optionValue} ${t('days')}`
    : option.optionValue
}

const OptionLabel = ({ option, t }) => {
  const descriptionKey = `institution_option.description.${option.optionType.type}`
  const description = t(descriptionKey)
  const hasDescription = description !== descriptionKey

  return (
    <div>
      {option.optionType.isPremium && (<PremiumIcon className='pdr-4' />)}
      <Tooltip title={description}>
        <span style={{ marginRight: '10px' }}>{t(`institution_option.type.${option.optionType.type}`)}</span>
      </Tooltip>
      {hasDescription && (
        <Tooltip title={description}>
          <FontAwesomeIcon icon={faQuestionCircle} color='#1890ff' />
        </Tooltip>
      )}
    </div>
  )
}

export const STRING_FIELD = 0
export const BOOLEAN_FIELD = 1

const InstitutionOptionTable = () => {
  const { institutionOptions, loading, updateOption } = useContext(InstitutionContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [optionToUpdate, setOptionToUpdate] = useState(null)
  const [warningModal, setWarningModal] = useState({ visible: false, text: '' })

  const options = useMemo(() => institutionOptions.sort((a, b) => {
    return (
      t(`institution_option.category.${a.optionType.category}`).localeCompare(t(`institution_option.category.${b.optionType.category}`)) ||
      t(`institution_option.type.${a.optionType.type}`).localeCompare(t(`institution_option.type.${b.optionType.type}`))
    )
  }).reduce((acc, option) => {
    const optionType = option.optionType

    if (!optionType.isHidden) {
      const optionCategory = optionType.category

      if (!acc.optionsByCategory[optionCategory]) {
        acc.optionsByCategory[optionCategory] = []
      }

      acc.optionsByCategory[optionCategory].push(option)
      acc.optionTypesMap[optionType.id] = option.optionEnabled
    }

    return acc
  }, { optionsByCategory: {}, optionTypesMap: {} }), [institutionOptions, t])

  const patchOption = (option) => {
    updateInstitutionOption(user, option, { optionEnabled: option.optionEnabled, optionValue: option.optionValue }).then(({ data }) => {
      updateOption(data)
      onSuccess(t('institution_option.update.success'))
    }).catch(() => {
      onError(t('institution_option.update.error'))
    })
  }

  const updateDataValue = (data, e) => {
    if (data.type === InstitutionOptions.DRAFT_INTERNSHIPS && !e) {
      setWarningModal({
        visible: true,
        text: 'If you deactivate the draft internship requests functionnality, all the pre-accepted internships will be revert back to created internships.',
        callback: () => {
          setWarningModal({ visible: false, text: '' })
          patchOption({ ...data, optionEnabled: e })
        }
      })
    } else {
      patchOption({ ...data, optionEnabled: e })
    }
  }

  const handleOptionValueUpdate = value => {
    if (!value.optionValue) {
      setOptionToUpdate(null)
      patchOption({ ...optionToUpdate, optionValue: value })
    } else {
      patchOption(value)
    }
  }

  return (
    <div className='options-table'>
      {loading && (
        <div className='center'>
          <div className='loading-icon black' />
        </div>
      )}
      {!loading && institutionOptions.length > 0 && (
        <Collapse>
          {Object.keys(options.optionsByCategory).map(category => (
            <Collapse.Panel header={t(`institution_option.category.${category}`)} key={category}>
              <List>
                {options.optionsByCategory[category].map(option => (
                  <List.Item key={option.optionType.id}>
                    <OptionLabel option={option} t={t} />
                    {OPTIONS_WITH_CUSTOM_VALUES.includes(option.optionType.type) && (
                      <div>
                        {option?.optionType?.type !== InstitutionOptions.SEND_MATCHING_HOSPITAL_EMAIL && `${t('common.value')}: ${displayOptionValue(option, t)}`}
                        <IconButton icon={faEdit} onClick={() => setOptionToUpdate(option)} />
                      </div>
                    )}
                    <Switch
                      disabled={!option.optionType.isEditable}
                      checked={option.optionEnabled}
                      onChange={(e) => updateDataValue(option, e)}
                    />
                  </List.Item>
                ))}
              </List>
            </Collapse.Panel>
          ))}
        </Collapse>
      )}
      {!(loading || institutionOptions.length > 0) && (
        <span> {t('No data is currently available for this page.')} </span>
      )}
      <OptionValueModal
        option={optionToUpdate}
        title={t("Update option's value")}
        onCancel={() => setOptionToUpdate(null)}
        onOk={handleOptionValueUpdate}
      />
      <Modal
        visible={warningModal.visible}
        title={t('Module activation/deactivation warning')}
        onOk={() => warningModal.callback()}
        onCancel={() => setWarningModal({ visible: false, text: '' })}
      >
        {t(warningModal.text)}
      </Modal>
    </div>
  )
}

export default InstitutionOptionTable
